import {useEffect, useMemo, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import QRCodeStyling, {type Options} from 'qr-code-styling'
import {buildQrOptions, type QrLogoState, type QrStyleState} from '../../../common/utils/qrRenderer'

interface QrPreviewProps {
  data: string
  style: QrStyleState
  logo: QrLogoState
}

const PREVIEW_SIZE_PX = 640
// Safety net for the draw-completion wait below — an image that never fires
// load/error (rare, but not impossible for a malformed upload) must not wedge
// the queue forever.
const DRAW_WAIT_TIMEOUT_MS = 4000

const waitForDraw = (qr: QRCodeStyling): Promise<void> =>
  Promise.race([
    Promise.resolve(qr._canvasDrawingPromise),
    new Promise<void>((resolve) => setTimeout(resolve, DRAW_WAIT_TIMEOUT_MS)),
  ])

const QrPreview = ({data, style, logo}: QrPreviewProps) => {
  const {t} = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const qrRef = useRef<QRCodeStyling | null>(null)
  const pendingRef = useRef<Options | null>(null)
  const drawingRef = useRef(false)

  const hasData = data.trim().length > 0
  const options = useMemo(() => buildQrOptions(data, style, logo, PREVIEW_SIZE_PX), [data, style, logo])

  useEffect(() => {
    if (!hasData) {
      pendingRef.current = null
      containerRef.current?.replaceChildren()
      qrRef.current = null
      return
    }

    if (!qrRef.current) {
      qrRef.current = new QRCodeStyling(options)
      if (containerRef.current) qrRef.current.append(containerRef.current)
      return
    }

    // `.update()` replaces the library's internal canvas and redraws it via an
    // async SVG→image→canvas pipeline. Firing a second `.update()` before the
    // first one's pipeline finishes lets them race: both eventually draw onto
    // whichever canvas is *current* at completion time, so an older (slower —
    // e.g. a large uploaded logo image) call can finish AFTER a newer, faster
    // one and paint a stale frame on top, or even leave the canvas blank if it
    // times out mid-draw. Queue instead: only ever run one `.update()` at a
    // time, keep just the latest pending options while one is in flight, and
    // apply that next once the current draw settles.
    pendingRef.current = options
    // If a drain loop is already running (started by an earlier effect
    // invocation), it will pick up this newer `pendingRef.current` on its next
    // iteration — don't start a second loop.
    if (drawingRef.current) return

    const drainQueue = async () => {
      drawingRef.current = true
      while (pendingRef.current && qrRef.current) {
        const next = pendingRef.current
        pendingRef.current = null
        qrRef.current.update(next)
        await waitForDraw(qrRef.current)
      }
      drawingRef.current = false
    }
    drainQueue()
  }, [hasData, options])

  return (
    <div className={`qrg-preview-stage${style.bgTransparent ? ' is-transparent' : ''}`}>
      <div ref={containerRef} className="qrg-preview-canvas-wrap" />
      {!hasData && <p className="qrg-preview-placeholder">{t('qrGenerator.preview.empty')}</p>}
    </div>
  )
}

export default QrPreview
